import { api } from "./api";

export interface Tenant {
  id: string;
  name: string;
  business_reg_no: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  industry: string;
  fiscal_year_start_month: number;
  base_currency: string;
  ui_language: string;
  vat_registered: boolean;
  vat_registration_no: string | null;
  tin: string | null;
  invoice_branch_code: string;
  place_of_supply: string | null;
  vat_filing_basis: string;
  sscl_liable: boolean;
  sscl_sector: string | null;
  payroll_active: boolean;
  plan: string;
  plan_valid_until: string | null;
  billing_cycle: string | null;
  trial_ends_at: string | null;
  trial: {
    ends_at: string | null;
    days_left: number | null;
    is_expired: boolean;
    applies: boolean;
  };
  plan_features: {
    payroll: boolean;
    multiCurrency: boolean;
    customBranding: boolean;
    whiteGlove: boolean;
  } | null;
  plan_limits: {
    max_invoices_per_month: number | null;
  };
  subscription_status: "active" | "past_due" | null;
  subscription_period_end: string | null;
  onboarding_step: "welcome" | "business" | "industry" | "tax" | "ready";
  onboarding_completed_at: string | null;
  bill_approval_threshold_cents: string | null;
  payslip_default_channel: "whatsapp" | "email" | "whatsapp_email" | "none";
  cash_flow_method: "direct" | "indirect";
  vat_recon_default: "claim" | "hold";
  personal_relief_threshold_cents: string;
  invoice_number_prefix: string | null;
  invoice_logo_path: string | null;
  invoice_footer_html: string | null;
  invoice_bank_details: string | null;
  invoice_payment_instructions: string | null;
  created_at: string;
  updated_at: string;
}

// API accepts camelCase keys (matches backend Zod schemas).
export interface TenantPatch {
  name?: string;
  businessRegNo?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  industry?: string;
  fiscalYearStartMonth?: number;
  baseCurrency?: string;
  uiLanguage?: "en" | "si" | "ta";
  vatRegistered?: boolean;
  vatRegistrationNo?: string | null;
  tin?: string | null;
  invoiceBranchCode?: string;
  placeOfSupply?: string | null;
  vatFilingBasis?: "accrual" | "cash";
  ssclLiable?: boolean;
  payrollActive?: boolean;
  billApprovalThresholdCents?: string | number | null;
  payslipDefaultChannel?: "whatsapp" | "email" | "whatsapp_email" | "none";
  cashFlowMethod?: "direct" | "indirect";
  vatReconDefault?: "claim" | "hold";
  personalReliefThresholdCents?: string | number;
  invoiceNumberPrefix?: string | null;
  invoiceLogoPath?: string | null;
  invoiceFooterHtml?: string | null;
  invoiceBankDetails?: string | null;
  invoicePaymentInstructions?: string | null;
}

export const tenantApi = {
  getMe: () => api.get<Tenant>("/tenants/me"),
  patch: (body: TenantPatch) => api.patch<Tenant>("/tenants/me", body),
  setStep: (step: Tenant["onboarding_step"]) =>
    api.post<Tenant>("/tenants/me/onboarding/step", { step }),
  setIndustry: (industry: string) =>
    api.post<{ tenant: Tenant; accounts_seeded: number }>(
      "/tenants/me/onboarding/industry",
      { industry }
    ),
};

export interface CoaTemplate {
  key: string;
  label: { letter: string; name: string; sub: string };
  account_count: number;
  preview: Array<{ code: string; name: string; type: string; is_root: boolean; depth: number }>;
}

export interface NewAccountInput {
  code: string;
  name: string;
  accountType: "asset" | "liability" | "equity" | "revenue" | "expense";
  statementSection: string;
  parentId?: string | null;
  description?: string | null;
}

export const coaApi = {
  templates: () => api.get<{ templates: CoaTemplate[] }>("/coa/templates"),
  template: (industry: string) =>
    api.get<{
      industry: string;
      label: CoaTemplate["label"];
      accounts: Array<{ code: string; name: string; type: string; parent_code: string | null; is_system: boolean; depth: number }>;
    }>(`/coa/templates/${industry}`),
  mine: () =>
    api.get<{
      sections?: string[];
      accounts: Array<{
        id: string;
        code: string;
        name: string;
        type: string;
        statement_section?: string | null;
        parent_id: string | null;
        is_system: boolean;
      }>;
    }>("/coa"),
  create: (body: NewAccountInput) => api.post<{ id: string }>("/coa", body),
  update: (id: string, body: Partial<{ name: string; statementSection: string | null }>) =>
    api.patch<{ id: string }>(`/coa/${id}`, body),
};

export interface SetupTask {
  id: string;
  key: string;
  title: string;
  description: string | null;
  completed_at: string | null;
}

export const setupTasksApi = {
  list: () => api.get<{ tasks: SetupTask[] }>("/setup-tasks"),
  set: (key: string, completed: boolean) =>
    api.patch<SetupTask>(`/setup-tasks/${key}`, { completed }),
};
